import { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

import DataGrid from 'app/components/DataGrid';
import useResource from 'app/hooks/useResource';
import AccountPricingModal from './AccountPricingModal';

import {
  getAccountPricings,
  setAccountPricingParams
} from 'app/actions/billings';

import {
  createAccountPricing,
  updateAccountPricing,
  destroyAccountPricing,
  getAccountPricings as exportAccountPricings
} from 'app/services/billings';

import {
  getAccounts
} from 'app/services/accounts';

import {
  accountPricings as resource
} from 'app/resources';

import {
  formatPascalCase,
  formatCurrency
} from 'app/utils/valueFormatters';

import {
  formatOptions,
  subscriptionOptions
} from 'app/utils/options';

const AccountPricing = (props) => {
  const { getAccountPricings, setAccountPricingParams } = props;

  const getAccountOptions = async (page = 1, perPage = 100, filter = null) => {
    let payload = {
      "page": page,
      "per_page": perPage,
      "sort_model": []
    }

    if (filter) {
      payload.filter_model = {
        "name": [
          { "filterClass": "text", "filterType": "contains", "value": filter }
        ]
      }
    }

    let resp = await getAccounts(payload);

    return formatOptions(resp.records)
  }

  const getAllAccounts = (page = 1, per_page = 100, accounts = []) => {
    return getAccounts({ page: page, per_page: per_page })
      .then(resp => {
        accounts = accounts.concat(resp.records);

        if (resp.records.length < per_page) {
          return Promise.resolve(accounts);
        } else {
          return getAllAccounts(page + 1, per_page, accounts);
        }
      });
  }

  const exportCsv = async (row) => {
    let params = {
      page: 0,
      per_page: 1000,
      is_export: true
    }

    const filterModel = getFilterModel();
    if (filterModel) {
      params.filter_model = filterModel;
    }

    exportAccountPricings(params).then((resp => resp.blob())).then((blob) => {
      const elem = window.document.createElement('a');
      elem.href = window.URL.createObjectURL(blob);
      elem.download = `account_pricing_${new Date().toISOString()}.csv`;
      document.body.appendChild(elem);
      elem.click();
      document.body.removeChild(elem);
    })
  }

  // load needed resources on first render
  const [accounts, setAccounts] = useState([]);
  const [accountOptions, setAccountOptions] = useState([]);

  const header = 'Account Pricings';
  const getRowData = getAccountPricings;
  const addButtonLabel = 'Add Account Pricing';
  const columnOrder = ['account_name', 'effectivity_date', 'per_image_uploaded_rate', 'per_image_sold_rate',
    'per_prepaid_customer_record_rate', 'per_event_rate',
    'per_kiosk_transaction_rate', 'per_mobile_transaction_rate', 'per_print_store_sale_rate',
    'per_kiosk_transaction_percentage_rate', 'per_mobile_transaction_percentage_rate', 'per_print_store_sale_percentage_rate',
    'subscription', 'subscription_rate'];
  const visibleColumns = columnOrder;

  const {
    onAdd,
    onEdit,
    formIsOpen,
    currentRecord,
    onDataGridLoad,
    onFormSubmit,
    onFormClose,
    onDestroy,
    showFilters,
    refreshGrid,
    getFilterModel
  } = useResource({
    resource,
    getResource: getAccountPricings,
    createResource: createAccountPricing,
    updateResource: updateAccountPricing,
    destroyResource: destroyAccountPricing,
    setResourceParams: setAccountPricingParams,
  });

  useEffect(() => {
    getAllAccounts().then(accounts => {
      setAccounts(accounts);
      setAccountOptions(formatOptions(accounts));
      // TODO: Not really ideal since this makes a 2nd call to account billing API
      // but ag-grid's refreshCells wasn't causing the cells to re-render once the accounts had been loaded
      refreshGrid();
    });
  }, []);

  const columnDefOverrides = visibleColumns.map((col) => {
    if (col === 'subscription') {
      return {
        field: 'subscription',
        headerName: 'Subscription',
        dataValueFormatter: (data, value) => {
          return subscriptionOptions.find((i) => i.value === value)?.label;
        },
        quickEdit: true,
        inputType: 'select',
        inputOptions: subscriptionOptions
      }

    } else if (col === 'account_id') {
      return {
        field: 'account_id',
        headerName: 'Account Name',
        dataValueFormatter: (data, value) => {
          return accountOptions.find((i) => i.value === data.account_id)?.label;
        },
        quickEdit: true,
        inputType: 'autocomplete',
        getInputOptions: getAccountOptions,
        selectedOptionFormatter: data => formatOptions([data.account_id])[0]
      }
    } else if (col.includes('percentage_rate')) {
      return {
        field: col,
        dataValueFormatter: (data, value) => {
          return value + "%";
        },
        quickEdit: true,
        headerName: formatPascalCase(col),
        inputType: 'number'
      }
    } else if (col.includes('rate')) {
      return {
        field: col,
        dataValueFormatter: (data, value) => {
          return formatCurrency(value, data.currency);
        },
        quickEdit: true,
        headerName: formatPascalCase(col),
        inputType: 'number'
      }
    } else if (col === 'effectivity_date') {
      return {
        field: 'effectivity_date',
        headerName: 'Effectivity Date',
        minWidth: 16 * 16, // 16rem
        inputType: 'date',
        renderFields: [
          { field: 'start_date', type: 'date' },
          { field: 'end_date', type: 'date', dateStyle: 'short', prefix: 'Until ' }
        ]
      }
    }
    else {
      return {
        field: col,
        quickEdit: true,
        headerName: formatPascalCase(col)
      }
    }
  })

  const destroyAction = (row) => {
    return destroyAccountPricing(row.id)
  }

  const actions = [
    { key: 'destroy', label: 'Delete Account Pricing', callback: destroyAction }
  ]

  const buttons = [
    {
      key: 'export',
      icon: <FileDownloadIcon />,
      label: 'CSV',
      onClick: exportCsv
    }
  ]

  return <div>
    <DataGrid
      resource={resource}
      header={header}
      getRowData={getRowData}
      addButtonLabel={addButtonLabel}
      onAdd={onAdd}
      showEdit={true}
      onEdit={onEdit}
      showQuickEdit={true}
      showSelect={true}
      loadingMessage="Loading account pricing..."
      columnOrder={columnOrder}
      visibleColumns={visibleColumns}
      columnDefOverrides={columnDefOverrides}
      showFilters={showFilters}
      onDataGridLoad={onDataGridLoad}
      actions={actions}
      buttons={buttons}
    />
    <AccountPricingModal
      isOpen={formIsOpen}
      onClose={onFormClose}
      onFormSubmit={onFormSubmit}
      onDestroy={onDestroy}
      accountPricing={currentRecord}
    />
  </div>
}

const mapStateToProps = state => ({
});

export default connect(mapStateToProps, { getAccountPricings, setAccountPricingParams })(withRouter(AccountPricing));
